/**
 * ConditionReportFlow — multi-step walkaround inspection for staff
 *
 * Steps: Odometer → Fuel → Panel-by-panel damage walk → Photos → Signature → Confirm
 * Reuses staff portal session token (X-Staff-Token header).
 */
import React, { useState, useRef, useEffect } from 'react';
import {
  X, ChevronRight, ChevronLeft, Loader2, Camera, CheckCircle2, AlertTriangle,
  Trash2, Edit3, FileSignature, Image as ImageIcon,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PANELS = [
  { key: 'front',          label: 'Front Bumper / Fascia' },
  { key: 'rear',           label: 'Rear Bumper / Fascia' },
  { key: 'driver_side',    label: 'Driver Side (Left)' },
  { key: 'passenger_side', label: 'Passenger Side (Right)' },
  { key: 'hood',           label: 'Hood' },
  { key: 'trunk',          label: 'Trunk / Tailgate' },
  { key: 'roof',           label: 'Roof' },
  { key: 'interior',       label: 'Interior' },
];

const FUEL_OPTIONS = ['empty', '1/4', '1/2', '3/4', 'full'];

export default function ConditionReportFlow({ item, mode, token, staffName, onClose, onComplete }) {
  // mode: 'pre' | 'post'
  const [step, setStep] = useState(0);
  const [reportId, setReportId] = useState(null);
  const [mileage, setMileage] = useState('');
  const [fuel, setFuel] = useState('full');
  const [panels, setPanels] = useState(() => Object.fromEntries(PANELS.map(p => [p.key, { damage: false, notes: '', photos: [] }])));
  const [generalNotes, setGeneralNotes] = useState('');
  const [exteriorClean, setExteriorClean] = useState(true);
  const [interiorClean, setInteriorClean] = useState(true);
  const [signatureBy, setSignatureBy] = useState(item?.client || '');
  const [signatureDataUrl, setSignatureDataUrl] = useState('');
  const [busy, setBusy] = useState(false);

  // Build steps array dynamically
  const steps = [
    { id: 'odometer', label: 'Odometer' },
    { id: 'fuel',     label: 'Fuel' },
    ...PANELS.map(p => ({ id: `panel-${p.key}`, label: p.label, panel: p.key })),
    { id: 'general',  label: 'General' },
    { id: 'signature', label: 'Signature' },
  ];
  const current = steps[step];
  const total = steps.length;

  // Create a draft report when the flow opens so subsequent saves can patch it
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/condition-reports`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Staff-Token': token },
          body: JSON.stringify({
            type: mode,
            booking_id: item?.id,
            booking_source: item?.source,
            vehicle_id: item?.vehicle_id || '',
            vehicle_name: item?.vehicle || '',
            client_name: item?.client || '',
          }),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || 'Failed to start report');
        if (!cancelled) setReportId(d.report_id);
      } catch (e) {
        if (!cancelled) { toast.error(e.message); onClose(); }
      }
    })();
    return () => { cancelled = true; };
  }, [mode, item, token, onClose]);

  const togglePanel = (key, field, value) => {
    setPanels(p => ({ ...p, [key]: { ...p[key], [field]: value } }));
  };

  const addPhotoToPanel = async (key, file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    form.append('category', 'condition-report');
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/uploads/image`, { method: 'POST', body: form });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Upload failed');
      togglePanel(key, 'photos', [...(panels[key].photos || []), { url: d.url, file_id: d.file_id, ts: new Date().toISOString() }]);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const removePhoto = (key, idx) => {
    const next = (panels[key].photos || []).filter((_, i) => i !== idx);
    togglePanel(key, 'photos', next);
  };

  const next = () => setStep(s => Math.min(s + 1, total - 1));
  const back = () => setStep(s => Math.max(s - 1, 0));

  const save = async (signed = false) => {
    if (!reportId) { toast.error('Report not initialized'); return; }
    setBusy(true);
    try {
      const body = {
        mileage: mileage ? Number(mileage) : null,
        fuel_level: fuel,
        panels,
        exterior_clean: exteriorClean,
        interior_clean: interiorClean,
        general_notes: generalNotes,
        status: signed ? 'signed' : 'draft',
      };
      if (signed) {
        body.signature_dataurl = signatureDataUrl;
        body.signed_by = signatureBy || staffName;
        body.signed_at = new Date().toISOString();
      }
      const r = await fetch(`${API}/api/condition-reports/${reportId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', 'X-Staff-Token': token },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Save failed');
      }
      if (signed) {
        toast.success('Condition report signed & saved', { position: 'top-center' });
        onComplete?.();
      }
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black z-[60] flex flex-col" data-testid="condition-report-flow">
      {/* Header */}
      <div className="sticky top-0 bg-black border-b border-gray-900 px-5 py-3 flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.3em] text-red-500 font-bold">{mode === 'pre' ? 'Pre-Rental' : 'Post-Rental'} Inspection</p>
          <p className="text-sm font-bold truncate mt-0.5">{item?.vehicle} · {item?.client}</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-white p-2" data-testid="cr-close">
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Progress */}
      <div className="px-5 py-2 border-b border-gray-900">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-gray-500">
          <span>Step {step + 1} of {total}</span>
          <span className="font-bold text-white">{current.label}</span>
        </div>
        <div className="h-1 w-full bg-gray-900 rounded-full mt-1.5">
          <div className="h-1 bg-red-500 rounded-full transition-all" style={{ width: `${((step + 1) / total) * 100}%` }} />
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 py-6">
        {current.id === 'odometer' && (
          <div className="space-y-3" data-testid="cr-step-odometer">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 font-bold">Current Mileage</p>
            <input
              type="number" inputMode="numeric" value={mileage} onChange={e => setMileage(e.target.value)}
              placeholder="42891" autoFocus
              className="w-full h-20 px-4 text-4xl font-black text-center bg-gray-950 border-2 border-gray-800 rounded-lg focus:border-red-500 outline-none font-mono tabular-nums"
              data-testid="cr-mileage"
            />
            <p className="text-xs text-gray-500">Enter the odometer reading shown right now.</p>
          </div>
        )}

        {current.id === 'fuel' && (
          <div className="space-y-3" data-testid="cr-step-fuel">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 font-bold">Fuel Level</p>
            <div className="grid grid-cols-5 gap-2">
              {FUEL_OPTIONS.map(f => (
                <button
                  key={f}
                  onClick={() => setFuel(f)}
                  className={`h-16 rounded-lg text-xs font-bold uppercase ${fuel === f ? 'bg-red-600 text-white' : 'bg-gray-950 text-gray-300 border border-gray-800'}`}
                  data-testid={`cr-fuel-${f.replace('/', '-')}`}
                >{f}</button>
              ))}
            </div>
          </div>
        )}

        {current.id.startsWith('panel-') && (
          <PanelStep
            panelKey={current.panel}
            label={current.label}
            state={panels[current.panel]}
            onToggleDamage={(v) => togglePanel(current.panel, 'damage', v)}
            onNotes={(v) => togglePanel(current.panel, 'notes', v)}
            onAddPhoto={(file) => addPhotoToPanel(current.panel, file)}
            onRemovePhoto={(i) => removePhoto(current.panel, i)}
            busy={busy}
          />
        )}

        {current.id === 'general' && (
          <div className="space-y-4" data-testid="cr-step-general">
            <ToggleRow label="Exterior clean" value={exteriorClean} onChange={setExteriorClean} testid="cr-exterior" />
            <ToggleRow label="Interior clean" value={interiorClean} onChange={setInteriorClean} testid="cr-interior" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 font-bold mb-1.5">General Notes</p>
              <textarea
                value={generalNotes} onChange={e => setGeneralNotes(e.target.value)} rows={4}
                placeholder="Anything else to capture about the overall condition…"
                className="w-full px-3 py-2 bg-gray-950 border border-gray-800 rounded-lg text-sm focus:border-red-500 outline-none"
                data-testid="cr-general-notes"
              />
            </div>
          </div>
        )}

        {current.id === 'signature' && (
          <SignatureStep
            value={signatureDataUrl} onChange={setSignatureDataUrl}
            signedBy={signatureBy} setSignedBy={setSignatureBy}
          />
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-900 bg-black px-5 py-3 flex items-center gap-3">
        <button
          onClick={back}
          disabled={step === 0 || busy}
          className="h-12 px-4 rounded-lg bg-gray-900 hover:bg-gray-800 disabled:opacity-30 text-xs uppercase font-bold flex items-center gap-1"
          data-testid="cr-back"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        <div className="flex-1" />
        {step === total - 1 ? (
          <button
            onClick={() => save(true)}
            disabled={busy || !signatureDataUrl}
            className="h-12 px-6 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-sm uppercase font-black tracking-widest flex items-center gap-2"
            data-testid="cr-finish"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
            Finish &amp; Sign
          </button>
        ) : (
          <button
            onClick={async () => { await save(false); next(); }}
            disabled={busy}
            className="h-12 px-5 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-40 text-sm uppercase font-bold flex items-center gap-1"
            data-testid="cr-next"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Next <ChevronRight className="h-4 w-4" /></>}
          </button>
        )}
      </div>
    </div>
  );
}

const ToggleRow = ({ label, value, onChange, testid }) => (
  <button
    onClick={() => onChange(!value)}
    data-testid={testid}
    className={`w-full h-14 px-4 rounded-lg border flex items-center justify-between ${value ? 'bg-emerald-900/30 border-emerald-700' : 'bg-gray-950 border-gray-800'}`}
  >
    <span className="text-sm font-semibold">{label}</span>
    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded ${value ? 'bg-emerald-700 text-emerald-100' : 'bg-gray-800 text-gray-400'}`}>{value ? 'Yes' : 'No'}</span>
  </button>
);

function PanelStep({ panelKey, label, state, onToggleDamage, onNotes, onAddPhoto, onRemovePhoto, busy }) {
  const fileRef = useRef(null);

  return (
    <div className="space-y-3" data-testid={`cr-panel-${panelKey}`}>
      <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 font-bold">{label}</p>

      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => onToggleDamage(false)}
          className={`h-16 rounded-lg flex flex-col items-center justify-center gap-1 ${!state.damage ? 'bg-emerald-700 text-white' : 'bg-gray-950 text-gray-400 border border-gray-800'}`}
          data-testid={`cr-${panelKey}-ok`}
        >
          <CheckCircle2 className="h-5 w-5" />
          <span className="text-xs font-bold uppercase tracking-widest">No Damage</span>
        </button>
        <button
          onClick={() => onToggleDamage(true)}
          className={`h-16 rounded-lg flex flex-col items-center justify-center gap-1 ${state.damage ? 'bg-yellow-600 text-white' : 'bg-gray-950 text-gray-400 border border-gray-800'}`}
          data-testid={`cr-${panelKey}-damage`}
        >
          <AlertTriangle className="h-5 w-5" />
          <span className="text-xs font-bold uppercase tracking-widest">Damage</span>
        </button>
      </div>

      {state.damage && (
        <textarea
          value={state.notes} onChange={e => onNotes(e.target.value)} rows={2}
          placeholder="Describe what's damaged (scratch, dent, scuff…)"
          className="w-full px-3 py-2 bg-gray-950 border border-gray-800 rounded-lg text-sm focus:border-yellow-500 outline-none"
          data-testid={`cr-${panelKey}-notes`}
        />
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 font-bold flex items-center gap-1.5">
            <ImageIcon className="h-3 w-3" /> Photos ({state.photos.length})
          </p>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="h-9 px-3 rounded-lg bg-gray-900 hover:bg-gray-800 text-xs font-bold uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-50"
            data-testid={`cr-${panelKey}-add-photo`}
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />} Add
          </button>
          <input
            ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onAddPhoto(f); e.target.value = ''; }}
          />
        </div>
        {state.photos.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {state.photos.map((p, i) => (
              <div key={i} className="relative aspect-square bg-gray-950 rounded-lg overflow-hidden border border-gray-800">
                <img src={`${API}${p.url}`} alt={`Panel ${panelKey} ${i + 1}`} className="w-full h-full object-cover" />
                <button
                  onClick={() => onRemovePhoto(i)}
                  className="absolute top-1 right-1 bg-black/70 rounded-full p-1 hover:bg-red-600"
                  aria-label="Remove photo"
                >
                  <Trash2 className="h-3 w-3 text-white" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SignatureStep({ value, onChange, signedBy, setSignedBy }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(!!value);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * dpr;
    canvas.height = canvas.offsetHeight * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2.5;
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, canvas.offsetWidth, canvas.offsetHeight);
      img.src = value;
    }
  }, [value]);

  const pointAt = (e) => {
    const canvas = canvasRef.current;
    const r = canvas.getBoundingClientRect();
    const ev = e.touches ? e.touches[0] : e;
    return { x: ev.clientX - r.left, y: ev.clientY - r.top };
  };

  const start = (e) => {
    e.preventDefault();
    setDrawing(true); setHasInk(true);
    const { x, y } = pointAt(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath(); ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = pointAt(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y); ctx.stroke();
  };
  const end = () => {
    setDrawing(false);
    onChange(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onChange('');
  };

  return (
    <div className="space-y-3" data-testid="cr-step-signature">
      <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 font-bold flex items-center gap-2">
        <FileSignature className="h-3 w-3" /> Client Signature
      </p>
      <input
        type="text" value={signedBy} onChange={e => setSignedBy(e.target.value)}
        placeholder="Signed by (name)"
        className="w-full h-12 px-3 bg-gray-950 border border-gray-800 rounded-lg text-sm font-semibold focus:border-red-500 outline-none"
        data-testid="cr-signed-by"
      />
      <div className="bg-gray-950 border-2 border-gray-800 rounded-lg overflow-hidden">
        <canvas
          ref={canvasRef}
          className="w-full h-48 touch-none cursor-crosshair"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          data-testid="cr-signature-pad"
        />
      </div>
      <div className="flex items-center justify-between">
        <p className="text-[10px] text-gray-500">Have the client sign above with finger or stylus.</p>
        <button onClick={clear} className="text-xs text-gray-400 hover:text-white flex items-center gap-1 underline" data-testid="cr-signature-clear">
          <Edit3 className="h-3 w-3" /> Clear
        </button>
      </div>
      {hasInk ? (
        <p className="text-xs text-emerald-400">Signature captured · ready to finish</p>
      ) : (
        <p className="text-xs text-yellow-500">Signature required to finish</p>
      )}
    </div>
  );
}
